import { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, Typography, IconButton, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserMetadata } from '../../context/UserMetadataContext';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';

const EntraIDModal = ({ isOpen, onClose, onSuccess }) => {
  const { userMetadata } = useUserMetadata();
  const { getTokens } = useAuth0Tokens();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

    if (!graphqlEndpoint) {
      setError('GraphQL endpoint configuration is missing');
      setIsLoading(false);
      return;
    }

    try {
      const { apiToken } = await getTokens();

      // Log GraphQL endpoint
      console.log('GraphQL Endpoint:', graphqlEndpoint);

      // Log the company name from metadata
      console.log('Company from metadata:', userMetadata?.company);

      // Validate required data
      if (!formData.tenantId.trim()) {
        throw new Error('Tenant ID is required');
      }
      if (!formData.clientId.trim()) {
        throw new Error('Client ID is required');
      }
      if (!formData.clientSecret.trim()) {
        throw new Error('Client Secret is required');
      }
      if (!userMetadata?.company) {
        throw new Error('Company information is missing');
      }

      // GraphQL mutation
      const mutation = `
        mutation ConfigureEntra($input: ConfigureEntraInput!) {
          configureEntra(input: $input) {
            success
            message
            integration {
              id
              tenantId
              integrationType
              orgName
              status
              createdAt
              updatedAt
            }
            errors
          }
        }
      `;

      const variables = {
        input: {
          tenantId: formData.tenantId.trim(),
          clientId: formData.clientId.trim(),
          clientSecret: formData.clientSecret.trim()
        }
      };

      console.log('GraphQL request:', { mutation, variables: { ...variables, input: { ...variables.input, clientSecret: '[REDACTED]' } } });

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      console.log('Response status:', response.status);

      const responseData = await response.json();
      console.log('GraphQL response:', responseData);

      // Check for GraphQL errors
      if (responseData.errors) {
        throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
      }

      // Check mutation response
      const result = responseData.data?.configureEntra;
      if (!result || !result.success) {
        throw new Error(result?.message || result?.errors?.[0] || 'Failed to configure Entra ID integration');
      }

      // Success!
      onSuccess({
        status: 'success',
        tenantId: formData.tenantId,
        message: 'Entra ID integration successful',
        ...responseData
      });

      onClose();
    } catch (err) {
      console.error('Integration error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'An error occurred while connecting to Entra ID');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleClose = useCallback(() => {
    setFormData({
      tenantId: '',
      clientId: '',
      clientSecret: ''
    });
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Connect Microsoft Entra ID</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="entra-form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Tenant ID"
            id="tenantId"
            name="tenantId"
            value={formData.tenantId}
            onChange={handleChange}
            placeholder="Enter your Entra ID Tenant ID"
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Client ID"
            id="clientId"
            name="clientId"
            value={formData.clientId}
            onChange={handleChange}
            placeholder="Enter your Client ID"
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Client Secret"
            type="password"
            id="clientSecret"
            name="clientSecret"
            value={formData.clientSecret}
            onChange={handleChange}
            placeholder="Enter your Client Secret"
            required
            sx={{ mb: 2 }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Connecting...' : 'Connect Entra ID'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntraIDModal;
